import { SyncAddition, SyncPreview, SyncRequest } from './work-classification-template.models';

/** Immutable selection state of the "Päivitä mallista" dialog. */
export interface SyncState {
  /** Template node ids of `additions` that will be added. */
  add: ReadonlySet<number>;
  /** Project node ids of `renames` that will be renamed. */
  rename: ReadonlySet<number>;
  /** Template node ids of `deletedInProject` the user chose to re-add on purpose. */
  deleted: ReadonlySet<number>;
  /** The explicit tick for billed renames. */
  confirmBilled: boolean;
}

/** Count of a preview field the backend may return as a number or a list. */
export function countOf(value: number | unknown[] | null | undefined): number {
  if (Array.isArray(value)) {
    return value.length;
  }
  return typeof value === 'number' ? value : 0;
}

/**
 * Pre-selection rules (backend "client rules for sync"): every addition and every rename without billed
 * entries is ticked; deletedInProject and billed renames are not; linkInstead / projectOnlyNodes /
 * orphanedLinks are informational and never part of the state.
 */
export function initialSyncState(preview: SyncPreview): SyncState {
  return {
    add: new Set(preview.additions.map(a => a.templateNodeId)),
    rename: new Set(preview.renames.filter(r => r.billedEntryCount === 0).map(r => r.projectNodeId)),
    deleted: new Set(),
    confirmBilled: false,
  };
}

/** True when `ancestor` is a strict path-prefix of `node` followed by a separator (not a longer name). */
function isPathAncestor(ancestor: SyncAddition, node: SyncAddition): boolean {
  if (ancestor.templateNodeId === node.templateNodeId || node.path.length <= ancestor.path.length) {
    return false;
  }
  if (!node.path.startsWith(ancestor.path)) {
    return false;
  }
  const rest = node.path.slice(ancestor.path.length).trimStart();
  return rest.length > 0 && /^[^\p{L}\p{N}]/u.test(rest);
}

/**
 * Template ids of the additions that must be added before `node` (its ancestors among the additions).
 * The preview gives only parentWillBeAdded and the display path, so ancestors are found by path prefix;
 * a backend `parentTemplateNodeId` is preferred when the preview carries one.
 */
export function additionAncestors(preview: SyncPreview, node: SyncAddition): SyncAddition[] {
  if (!node.parentWillBeAdded) {
    return [];
  }
  const byId = new Map(preview.additions.map(a => [a.templateNodeId, a]));
  const result: SyncAddition[] = [];
  let current: SyncAddition | undefined = node;
  while (current && current.parentWillBeAdded) {
    const explicit: number | null | undefined = current.parentTemplateNodeId;
    const cur: SyncAddition = current;
    const parent: SyncAddition | undefined =
      explicit != null
        ? byId.get(explicit)
        : preview.additions
            .filter(a => isPathAncestor(a, cur))
            // nearest ancestor = the longest matching path
            .sort((x, y) => y.path.length - x.path.length)[0];
    if (!parent || result.includes(parent)) {
      break;
    }
    result.push(parent);
    current = parent;
  }
  return result;
}

function without<T>(set: ReadonlySet<T>, remove: Iterable<T>): Set<T> {
  const next = new Set(set);
  for (const v of remove) {
    next.delete(v);
  }
  return next;
}

/** Ticking a child ticks its added ancestors; unticking a parent unticks its added descendants. */
export function toggleAddition(preview: SyncPreview, state: SyncState, templateNodeId: number, checked: boolean): SyncState {
  const node = preview.additions.find(a => a.templateNodeId === templateNodeId);
  if (!node) {
    return state;
  }
  if (checked) {
    const add = new Set(state.add);
    add.add(templateNodeId);
    for (const ancestor of additionAncestors(preview, node)) {
      add.add(ancestor.templateNodeId);
    }
    return { ...state, add };
  }
  const descendants = preview.additions
    .filter(a => additionAncestors(preview, a).some(anc => anc.templateNodeId === templateNodeId))
    .map(a => a.templateNodeId);
  return { ...state, add: without(state.add, [templateNodeId, ...descendants]) };
}

export function toggleAllAdditions(preview: SyncPreview, state: SyncState, checked: boolean): SyncState {
  return { ...state, add: new Set(checked ? preview.additions.map(a => a.templateNodeId) : []) };
}

export function toggleRename(preview: SyncPreview, state: SyncState, projectNodeId: number, checked: boolean): SyncState {
  const rename = new Set(state.rename);
  if (checked) {
    rename.add(projectNodeId);
  } else {
    rename.delete(projectNodeId);
  }
  const next = { ...state, rename };
  // The confirmation only exists while a billed rename is ticked; do not carry a stale tick.
  return selectedBilledCount(preview, next) === 0 ? { ...next, confirmBilled: false } : next;
}

export function toggleDeleted(state: SyncState, templateNodeId: number, checked: boolean): SyncState {
  const deleted = new Set(state.deleted);
  if (checked) {
    deleted.add(templateNodeId);
  } else {
    deleted.delete(templateNodeId);
  }
  return { ...state, deleted };
}

export function setConfirmBilled(state: SyncState, confirmed: boolean): SyncState {
  return { ...state, confirmBilled: confirmed };
}

/** Number of ticked renames that touch billed entries. */
export function selectedBilledCount(preview: SyncPreview, state: SyncState): number {
  return preview.renames.filter(r => r.billedEntryCount > 0 && state.rename.has(r.projectNodeId)).length;
}

export function selectedAddCount(state: SyncState): number {
  return state.add.size + state.deleted.size;
}

/** The primary button is enabled only with a selection and, when billed renames are ticked, their confirmation. */
export function canSubmitSync(preview: SyncPreview, state: SyncState): boolean {
  const something = selectedAddCount(state) + state.rename.size > 0;
  const billed = selectedBilledCount(preview, state);
  return something && (billed === 0 || state.confirmBilled);
}

/** The explicit selection body; the backend has no "apply all". Re-added deletedInProject nodes go in addTemplateNodeIds. */
export function buildSyncRequest(preview: SyncPreview, state: SyncState): SyncRequest {
  return {
    addTemplateNodeIds: [...state.add, ...state.deleted],
    renameProjectNodeIds: [...state.rename],
    confirmBilledRenames: selectedBilledCount(preview, state) > 0 && state.confirmBilled,
  };
}
